import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
  Description as PreviewIcon
} from '@mui/icons-material';
import { apiFetch } from '../../utils/api';
import { Logger } from '../../utils/logger';
import { BRAND_PURPLE, COLOR_GOOD, COLOR_POOR, BG_SURFACE, BG_ELEVATED } from '../../utils/colors';
import { generateClientIntroductionPDF } from '../../utils/generateDocumentPDF';

/**
 * ClientIntroductionModal - Generates and displays client-facing team introduction documents
 * 
 * This modal integrates with the Team Optimization system to create professional
 * team introduction documents suitable for client presentations and sales processes.
 */
const logger = new Logger('ClientIntroductionModal');

const ClientIntroductionModal = ({
  open,
  onClose,
  teamOptimizationResult,
  projectContext = {},
  clientContext = {},
  initialDocument = null,
  onGenerated = null
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [introductionDocument, setIntroductionDocument] = useState(
    initialDocument || teamOptimizationResult?.client_introduction || null
  );
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Load saved document when modal opens or config changes
  React.useEffect(() => {
    if (open) {
      const saved = initialDocument || teamOptimizationResult?.client_introduction || null;
      if (saved && !introductionDocument) setIntroductionDocument(saved);
    }
  }, [open, initialDocument, teamOptimizationResult]);

  const handleRegenerateIntroduction = () => {
    setIntroductionDocument(null);
    setError(null);
    // handleGenerateIntroduction will be called by the user clicking Generate
  };

  // Generate client introduction document
  const handleGenerateIntroduction = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      logger.debug('Generating client introduction with data:', {
        teamOptimizationResult: teamOptimizationResult ? 'present' : 'missing',
        projectContext,
        clientContext
      });

      const response = await apiFetch('team-optimization/client-introduction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          team_optimization_result: teamOptimizationResult,
          project_context: projectContext,
          client_context: clientContext
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'success') {
        setIntroductionDocument(data.document);
        if (onGenerated) onGenerated(data.document);
        logger.debug('Client introduction generated successfully');
      } else {
        throw new Error(data.error || 'Failed to generate client introduction');
      }
    } catch (err) {
      logger.error('Error generating client introduction:', err);
      setError(err.message || 'Failed to generate client introduction');
    } finally {
      setIsGenerating(false);
    }
  };

  // Copy content to clipboard
  const handleCopyToClipboard = async () => {
    if (!introductionDocument?.content) return;

    try {
      const content = formatDocumentForCopy(introductionDocument.content);
      await navigator.clipboard.writeText(content);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      logger.error('Failed to copy to clipboard:', err);
    }
  };

  // Format document content for copying
  const formatDocumentForCopy = (content) => {
    let formatted = '';
    
    // Executive Summary
    if (content.executive_summary) {
      formatted += `EXECUTIVE SUMMARY\n\n${content.executive_summary}\n\n`;
    }

    // Team Overview
    if (content.team_overview) {
      formatted += `TEAM OVERVIEW\n\n`;
      if (content.team_overview.headline) {
        formatted += `${content.team_overview.headline}\n\n`;
      }
      if (content.team_overview.key_differentiators) {
        formatted += `Key Differentiators:\n`;
        content.team_overview.key_differentiators.forEach(diff => {
          formatted += `• ${diff}\n`;
        });
        formatted += '\n';
      }
    }

    // Individual Profiles
    if (content.individual_profiles && content.individual_profiles.length > 0) {
      formatted += `TEAM MEMBER PROFILES\n\n`;
      content.individual_profiles.forEach(profile => {
        formatted += `${profile.name} - ${profile.role}\n`;
        formatted += `${profile.profile}\n`;
        if (profile.key_qualifications) {
          formatted += `Key Qualifications:\n`;
          profile.key_qualifications.forEach(qual => {
            formatted += `• ${qual}\n`;
          });
        }
        formatted += '\n';
      });
    }

    // Success Factors
    if (content.success_factors && content.success_factors.length > 0) {
      formatted += `SUCCESS FACTORS\n\n`;
      content.success_factors.forEach(factor => {
        formatted += `• ${factor}\n`;
      });
      formatted += '\n';
    }

    // Closing Statement
    if (content.closing_statement) {
      formatted += `${content.closing_statement}\n`;
    }

    return formatted;
  };

  // Download as Word document
  const handleDownload = () => {
    if (!introductionDocument?.content) return;

    // Generate Word document
    generateWordDocument(introductionDocument.content, introductionDocument.generated_date);
  };

  // Generate document download (text format)
  const generateWordDocument = async (content, generatedDate) => {
    try {
      // Create formatted text content
      let documentContent = '';
      
      // Title and date
      documentContent += '='.repeat(60) + '\n';
      documentContent += 'PROFESSIONAL TEAM INTRODUCTION\n';
      documentContent += '='.repeat(60) + '\n\n';
      documentContent += `Generated: ${new Date(generatedDate).toLocaleDateString()}\n\n`;
      
      // Use the existing format function
      const formattedContent = formatDocumentForCopy(content);
      documentContent += formattedContent;
      
      // Add footer
      documentContent += '\n' + '='.repeat(60) + '\n';
      documentContent += 'End of Document\n';
      documentContent += '='.repeat(60);

      // Create and download the file
      const blob = new Blob([documentContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `team-introduction-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (error) {
      logger.error('Error generating document:', error);
      // Fallback to basic text file
      const content = formatDocumentForCopy(introductionDocument.content);
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `team-introduction-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // Reset modal state when closed
  const handleClose = () => {
    setIntroductionDocument(null);
    setError(null);
    setCopySuccess(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: BG_SURFACE,
          color: 'white',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: `${BRAND_PURPLE}1A`,
        borderBottom: `1px solid ${BRAND_PURPLE}33`,
        py: 1.5
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
            Client Team Introduction
          </Typography>
          {introductionDocument?.generated_date && (
            <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mt: 0.25 }}>
              Generated {new Date(introductionDocument.generated_date).toLocaleString()}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {introductionDocument && !isGenerating && (
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleRegenerateIntroduction}
              variant="outlined"
              size="small"
              sx={{ borderColor: BRAND_PURPLE, color: BRAND_PURPLE, '&:hover': { borderColor: BRAND_PURPLE, bgcolor: `${BRAND_PURPLE}0D` } }}
            >
              Regenerate
            </Button>
          )}
          {introductionDocument?.content && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => generateClientIntroductionPDF(introductionDocument, projectContext, clientContext)}
              variant="outlined"
              size="small"
              sx={{ borderColor: BRAND_PURPLE, color: BRAND_PURPLE, '&:hover': { borderColor: BRAND_PURPLE, bgcolor: `${BRAND_PURPLE}0D` } }}
            >
              Download PDF
            </Button>
          )}
          {introductionDocument?.content && (
            <Tooltip title={copySuccess ? 'Copied!' : 'Copy to Clipboard'}>
              <IconButton onClick={handleCopyToClipboard} size="small" sx={{ color: copySuccess ? COLOR_GOOD : 'white' }}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={handleClose} sx={{ color: 'white' }} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Generation Controls */}
        {!introductionDocument && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Generate Professional Team Introduction
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: '#ccc' }}>
              Create a client-facing document highlighting your team's expertise,
              experience, and success factors for this engagement.
            </Typography>
            
            {/* Project Context Display */}
            {(projectContext.project_name || clientContext.client_name) && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: BG_ELEVATED, border: '1px solid #444' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: BRAND_PURPLE }}>
                  Context Information
                </Typography>
                {projectContext.project_name && (
                  <Chip 
                    label={`Project: ${projectContext.project_name}`} 
                    sx={{ mr: 1, mb: 1, bgcolor: '#444', color: 'white' }}
                  />
                )}
                {clientContext.client_name && (
                  <Chip 
                    label={`Client: ${clientContext.client_name}`} 
                    sx={{ mr: 1, mb: 1, bgcolor: '#444', color: 'white' }}
                  />
                )}
                {clientContext.industry && (
                  <Chip 
                    label={`Industry: ${clientContext.industry}`} 
                    sx={{ mr: 1, mb: 1, bgcolor: '#444', color: 'white' }}
                  />
                )}
              </Paper>
            )}

            <Button
              variant="contained"
              onClick={handleGenerateIntroduction}
              disabled={isGenerating || !teamOptimizationResult}
              startIcon={isGenerating ? <CircularProgress size={20} /> : <PreviewIcon />}
              sx={{
                bgcolor: BRAND_PURPLE,
                '&:hover': { bgcolor: `${BRAND_PURPLE}CC` },
                px: 4,
                py: 1.5
              }}
            >
              {isGenerating ? 'Generating...' : 'Generate Introduction'}
            </Button>

            {!teamOptimizationResult && (
              <Typography variant="caption" sx={{ display: 'block', mt: 2, color: COLOR_POOR }}>
                Team optimization data is required to generate introduction
              </Typography>
            )}
          </Box>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, bgcolor: `${COLOR_POOR}15`, color: COLOR_POOR }}>
            {error}
          </Alert>
        )}

        {/* Generated Document Display */}
        {introductionDocument && (
          <Box>
            {/* Document Content */}
            <Paper sx={{ p: 3, bgcolor: BG_ELEVATED, border: '1px solid #444' }}>
              {/* Executive Summary */}
              {introductionDocument.content?.executive_summary && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: BRAND_PURPLE, fontWeight: 'bold' }}>
                    Executive Summary
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 2 }}>
                    {introductionDocument.content.executive_summary}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 3, bgcolor: '#444' }} />

              {/* Team Overview */}
              {introductionDocument.content?.team_overview && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: BRAND_PURPLE, fontWeight: 'bold' }}>
                    Team Overview
                  </Typography>
                  {introductionDocument.content.team_overview.headline && (
                    <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic' }}>
                      {introductionDocument.content.team_overview.headline}
                    </Typography>
                  )}
                  {introductionDocument.content.team_overview.key_differentiators && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#ccc' }}>
                        Key Differentiators:
                      </Typography>
                      {introductionDocument.content.team_overview.key_differentiators.map((diff, index) => (
                        <Typography key={index} variant="body2" sx={{ mb: 0.5, pl: 2 }}>
                          • {diff}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 3, bgcolor: '#444' }} />

              {/* Individual Profiles */}
              {introductionDocument.content?.individual_profiles && introductionDocument.content.individual_profiles.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: BRAND_PURPLE, fontWeight: 'bold' }}>
                    Team Member Profiles
                  </Typography>
                  {introductionDocument.content.individual_profiles.map((profile, index) => (
                    <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: BG_SURFACE, border: '1px solid #333' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {profile.name} - {profile.role}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
                        {profile.profile}
                      </Typography>
                      {profile.key_qualifications && (
                        <Box>
                          <Typography variant="caption" sx={{ color: '#ccc', mb: 1, display: 'block' }}>
                            Key Qualifications:
                          </Typography>
                          {profile.key_qualifications.map((qual, qualIndex) => (
                            <Typography key={qualIndex} variant="caption" sx={{ display: 'block', pl: 1, mb: 0.5 }}>
                              • {qual}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}

              <Divider sx={{ my: 3, bgcolor: '#444' }} />

              {/* Success Factors */}
              {introductionDocument.content?.success_factors && introductionDocument.content.success_factors.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: BRAND_PURPLE, fontWeight: 'bold' }}>
                    Success Factors
                  </Typography>
                  {introductionDocument.content.success_factors.map((factor, index) => (
                    <Typography key={index} variant="body2" sx={{ mb: 1, pl: 2 }}>
                      • {factor}
                    </Typography>
                  ))}
                </Box>
              )}

              {/* Closing Statement */}
              {introductionDocument.content?.closing_statement && (
                <Box sx={{ mt: 3, p: 2, bgcolor: BG_SURFACE, border: '1px solid #333', borderRadius: 1 }}>
                  <Typography variant="body1" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
                    {introductionDocument.content.closing_statement}
                  </Typography>
                </Box>
              )}
            </Paper>

          </Box>
        )}
      </DialogContent>

    </Dialog>
  );
};

export default ClientIntroductionModal;
